import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query"

import { apiFetch } from "@/api/client"
import type { VerticalOption } from "@/lib/verticalCatalog"

export type { VerticalOption } from "@/lib/verticalCatalog"

/**
 * Hooks for the Offers page (T3.5). Covers:
 *   - `GET  /api/offers`              — paginated listing with filters
 *   - `POST /api/offers`              — create
 *   - `PUT  /api/offers/{id}`         — update (edit / archive / restore)
 *   - `GET  /api/pixels/my-pixels`    — user's pixels for the form
 *   - `GET  /api/adsmanager/users`    — user filter dropdown
 *   - `GET  /api/analytics/keywords`  — keyword analytics modal
 *   - `GET  /api/config/network-domains` — network domain config
 */

// ---------------------------------------------------------------------------
// Response types
// ---------------------------------------------------------------------------

export interface OfferMetrics {
  cost: number | null
  revenue: number | null
  profit: number | null
  roi: number | null
  gclicks: number | null
  g_clicks: number | null
  cpa: number | null
  rpc: number | null
  conversions: number | null
  clicks: number | null
  cpc: number | null
  cpm: number | null
  ctr: number | null
  cvr: number | null
  impressions: number | null
  visits: number | null
  second_flow_visits: number | null
  rpm: number | null
  flow_1_to_2: number | null
}

export interface Offer {
  id: number
  /** Opaque token for `/tracking/click?o=` — not the internal DB row id. */
  public_slug: string
  name: string
  network: string
  target_url: string | null
  partner_domain: string | null
  headline: string | null
  terms: string | null
  rac: string | null
  status: string
  pixel_db_id: number | null
  event_name: string | null
  vertical_slug: string | null
  vertical_name?: string | null
  adw_link_id: string | null
  created_at: string | null
  metrics?: OfferMetrics
  klow_article_id?: number | null
  klow_domain?: string | null
  klow_rsoc_template_id?: string | null
  klow_google_template_id?: string | null
  klow_pixel_id?: string | null
  klow_suggested_keywords?: string | null
  klow_ad_copy_text?: string | null
  klow_traffic_source?: string | null
  klow_article_slug?: string | null
  klow_article_url?: string | null
}

export interface OffersPagination {
  page: number
  page_size: number
  total: number
  pages: number
}

export interface OffersListResponse {
  success: boolean
  data: Offer[]
  pagination: OffersPagination
}

export interface OfferVerticalsResponse {
  success: boolean
  data: VerticalOption[]
}

export interface ClearVerticalResponse {
  success: boolean
  cleared: number
}

export interface OffersListParams {
  sort_by: string
  sort_order: string
  page: number
  page_size: number
  network?: string
  status?: string
  user_ids?: number[]
  start_date?: string
  end_date?: string
}

export interface UserFilterOption {
  id: number
  username: string
  role: string
  label?: string
}

export interface UsersFilterResponse {
  success: boolean
  users: UserFilterOption[]
}

export interface PixelOption {
  id: number
  name: string
  pixel_id?: string
}

export interface PixelsResponse {
  success: boolean
  data: PixelOption[]
}

export interface AdminPixelsResponse {
  success: boolean
  data: Array<
    PixelOption & {
      user_id: number
      owner_username: string
      created_at: string | null
    }
  >
}

export interface KeywordStat {
  keyword: string
  revenue: number | null
  gclicks: number | null
  second_flow_visits: number | null
  rpc: number | null
}

export interface KeywordsResponse {
  success: boolean
  data: KeywordStat[]
  debug?: unknown
}

export type NetworkDomains = Record<string, string[]>

// ---------------------------------------------------------------------------
// Query keys
// ---------------------------------------------------------------------------

export const OFFERS_QUERY_KEYS = {
  all: ["offers"] as const,
  list: (p: OffersListParams) => ["offers", "list", p] as const,
  verticals: () => ["offers", "verticals"] as const,
  pixels: () => ["offers", "pixels"] as const,
  users: () => ["offers", "users"] as const,
  keywords: (params: Record<string, string>) =>
    ["offers", "keywords", params] as const,
  networkDomains: () => ["offers", "network-domains"] as const,
} as const

// ---------------------------------------------------------------------------
// Hooks
// ---------------------------------------------------------------------------

function buildOffersUrl(params: OffersListParams): string {
  const qs = new URLSearchParams()
  qs.set("sort_by", params.sort_by)
  qs.set("sort_order", params.sort_order)
  qs.set("page", String(params.page))
  qs.set("page_size", String(params.page_size))
  if (params.network) qs.set("network", params.network)
  // Always send status — omitting it makes the API default to Active, so
  // "All" would silently drop Archived/Failed/etc.
  if (params.status) qs.set("status", params.status)
  if (params.user_ids && params.user_ids.length > 0) {
    for (const uid of params.user_ids) {
      qs.append("user_ids", String(uid))
    }
  }
  if (params.start_date) qs.set("start_date", params.start_date)
  if (params.end_date) qs.set("end_date", params.end_date)
  return `/api/offers?${qs.toString()}`
}

export function useOffersList(
  params: OffersListParams,
  options: { enabled?: boolean } = {},
) {
  return useQuery<OffersListResponse>({
    queryKey: OFFERS_QUERY_KEYS.list(params),
    queryFn: () => apiFetch<OffersListResponse>(buildOffersUrl(params)),
    enabled: options.enabled ?? true,
    retry: false,
  })
}

/** Distinct internal verticals visible to the user (team / org), for the offer form. */
export function useOfferVerticals(options: { enabled?: boolean } = {}) {
  return useQuery<OfferVerticalsResponse>({
    queryKey: OFFERS_QUERY_KEYS.verticals(),
    queryFn: () =>
      apiFetch<OfferVerticalsResponse>("/api/offers/verticals"),
    enabled: options.enabled ?? true,
    retry: false,
  })
}

/** Admin-only: clear a vertical slug from all offers (dedupe cleanup). */
export function useDeleteOfferVertical() {
  const qc = useQueryClient()
  return useMutation<ClearVerticalResponse, Error, string>({
    mutationFn: (slug) => {
      const qs = new URLSearchParams({ slug })
      return apiFetch<ClearVerticalResponse>(
        `/api/offers/verticals?${qs.toString()}`,
        { method: "DELETE" },
      )
    },
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: OFFERS_QUERY_KEYS.all })
      qc.invalidateQueries({ queryKey: OFFERS_QUERY_KEYS.verticals() })
    },
  })
}

export function useCreateOffer() {
  const qc = useQueryClient()
  return useMutation<Offer, Error, Record<string, unknown>>({
    mutationFn: (body) =>
      apiFetch<Offer>("/api/offers", { method: "POST", body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: OFFERS_QUERY_KEYS.all })
      qc.invalidateQueries({ queryKey: OFFERS_QUERY_KEYS.verticals() })
    },
  })
}

export function useUpdateOffer() {
  const qc = useQueryClient()
  return useMutation<
    Offer,
    Error,
    { offerId: number; body: Record<string, unknown> }
  >({
    mutationFn: ({ offerId, body }) =>
      apiFetch<Offer>(`/api/offers/${offerId}`, { method: "PUT", body }),
    onSuccess: () => {
      qc.invalidateQueries({ queryKey: OFFERS_QUERY_KEYS.all })
      qc.invalidateQueries({ queryKey: OFFERS_QUERY_KEYS.verticals() })
    },
  })
}

export function useMyPixels(
  options: { showAllForAdmin?: boolean; enabled?: boolean } = {},
) {
  const showAllForAdmin = options.showAllForAdmin ?? false
  return useQuery<PixelsResponse>({
    queryKey: OFFERS_QUERY_KEYS.pixels(),
    queryFn: async () => {
      if (showAllForAdmin) {
        const response = await apiFetch<AdminPixelsResponse>("/api/pixels")
        return {
          success: response.success,
          data: response.data.map((p) => ({
            id: p.id,
            name: p.name,
            pixel_id: p.pixel_id,
          })),
        }
      }
      return apiFetch<PixelsResponse>("/api/pixels/my-pixels")
    },
    enabled: options.enabled ?? true,
    retry: false,
  })
}

export function useUsersForFilter(options: { enabled?: boolean } = {}) {
  return useQuery<UsersFilterResponse>({
    queryKey: OFFERS_QUERY_KEYS.users(),
    queryFn: () =>
      apiFetch<UsersFilterResponse>("/api/adsmanager/users"),
    enabled: options.enabled ?? true,
    retry: false,
  })
}

export function useKeywordAnalytics(
  params: { offer_id?: number; start_date?: string; end_date?: string },
  options: { enabled?: boolean } = {},
) {
  const qs = new URLSearchParams()
  if (params.offer_id) qs.set("offer_id", String(params.offer_id))
  if (params.start_date) qs.set("start_date", params.start_date)
  if (params.end_date) qs.set("end_date", params.end_date)
  const key = Object.fromEntries(qs.entries())

  return useQuery<KeywordsResponse>({
    queryKey: OFFERS_QUERY_KEYS.keywords(key),
    queryFn: () =>
      apiFetch<KeywordsResponse>(`/api/analytics/keywords?${qs.toString()}`),
    enabled: options.enabled ?? true,
    retry: false,
  })
}

export function useNetworkDomains(options: { enabled?: boolean } = {}) {
  return useQuery<NetworkDomains>({
    queryKey: OFFERS_QUERY_KEYS.networkDomains(),
    queryFn: () => apiFetch<NetworkDomains>("/api/config/network-domains"),
    staleTime: 5 * 60 * 1000,
    enabled: options.enabled ?? true,
    retry: false,
  })
}

export function useInvalidateOffers() {
  const qc = useQueryClient()
  return () => qc.invalidateQueries({ queryKey: OFFERS_QUERY_KEYS.all })
}
